import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Container, TextField, Typography, Alert, Paper,
  Tabs, Tab, Divider, Grid, Chip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { login, registerWithApartment, forgotPassword } from '../services/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function Login() {
  const [tab, setTab] = useState(0);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Register state
  const [regForm, setRegForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    apartmentName: '',
    apartmentAddress: '',
    totalFlats: '',
    totalFloors: '',
    flatsPerFloor: '',
    flatNumber: '',
    openingFundBalance: ''
  });
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (!loginEmail) {
      setLoginError('Please enter your email or phone number');
      return;
    }

    // Auto-detect: if it's all digits and 10 chars, treat as phone; otherwise treat as email
    const isPhone = /^\d{10}$/.test(loginEmail);
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail);

    if (!isPhone && !isEmail) {
      setLoginError('Please enter a valid email address or 10-digit phone number');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        email: isEmail ? loginEmail : null,
        phone: isPhone ? loginEmail : null,
        password: loginPassword
      };
      const response = await login(payload);
      loginUser(response.data.token, response.data.user);
      navigate('/');
    } catch (err) {
      setLoginError(err.response?.data || 'Login failed. Please check your email/phone and password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');

    if (!forgotEmail) {
      setForgotError('Please enter your email or phone number');
      return;
    }

    // Determine if it's email or phone
    const isPhone = /^\d{10}$/.test(forgotEmail);
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail);

    if (!isPhone && !isEmail) {
      setForgotError('Please enter a valid email address or 10-digit phone number');
      return;
    }

    try {
      const payload = isPhone ? { phone: forgotEmail } : { email: forgotEmail };
      const response = await forgotPassword(payload);
      setForgotMessage(response.data.message || 'If the account exists, a reset link has been sent to your registered email.');
    } catch (err) {
      setForgotError(err.response?.data || 'Failed to send reset link. Please try again.');
    }
  };

  // Generate flat numbers based on floors and flats per floor
  const generateFlatNumbers = () => {
    const floors = parseInt(regForm.totalFloors);
    const flatsPerFloor = parseInt(regForm.flatsPerFloor);
    if (!floors || !flatsPerFloor) return [];
    const flats = [];
    for (let floor = 1; floor <= floors; floor++) {
      for (let flat = 1; flat <= flatsPerFloor; flat++) {
        flats.push(`${floor}${flat.toString().padStart(2, '0')}`);
      }
    }
    return flats;
  };

  // Auto-calculate total flats when floors/flatsPerFloor change
  const handleFloorChange = (field) => (e) => {
    const newForm = { ...regForm, [field]: e.target.value };
    const floors = parseInt(field === 'totalFloors' ? e.target.value : newForm.totalFloors);
    const flatsPerFloor = parseInt(field === 'flatsPerFloor' ? e.target.value : newForm.flatsPerFloor);
    if (floors && flatsPerFloor) {
      newForm.totalFlats = (floors * flatsPerFloor).toString();
    }
    setRegForm(newForm);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess('');

    if (regForm.password !== regForm.confirmPassword) {
      setRegError('Passwords do not match');
      return;
    }

    if (!regForm.fullName || !regForm.email || !regForm.phone || !regForm.password) {
      setRegError('Please fill all user details');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email)) {
      setRegError('Please enter a valid email address');
      return;
    }

    if (regForm.phone.length !== 10) {
      setRegError('Phone number must be exactly 10 digits');
      return;
    }

    if (!regForm.apartmentName || !regForm.apartmentAddress || !regForm.totalFlats || !regForm.flatNumber) {
      setRegError('Please fill all apartment details');
      return;
    }

    try {
      setSubmitting(true);
      const response = await registerWithApartment({
        fullName: regForm.fullName,
        email: regForm.email,
        phone: regForm.phone,
        password: regForm.password,
        apartmentName: regForm.apartmentName,
        apartmentAddress: regForm.apartmentAddress,
        totalFlats: parseInt(regForm.totalFlats),
        flatNumber: regForm.flatNumber,
        openingFundBalance: regForm.openingFundBalance ? parseFloat(regForm.openingFundBalance) : 0
      });
      loginUser(response.data.token, response.data.user);
      navigate('/');
    } catch (err) {
      setRegError(err.response?.data || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateRegForm = (field) => (e) => setRegForm({ ...regForm, [field]: e.target.value });

  const generatedFlats = generateFlatNumbers();

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundImage: 'url(/bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
    <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 8 }, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Apartment Expense Manager
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
            Manage your apartment expenses effortlessly
          </Typography>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} centered sx={{ mb: 1 }}>
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
            <Tab label="Forgot Password" />
          </Tabs>

          {/* LOGIN TAB */}
          <TabPanel value={tab} index={0}>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth label="Email or Phone Number" margin="normal" required
                value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Enter your email or 10-digit phone number"
              />
              <TextField
                fullWidth label="Password" type="password" margin="normal" required
                value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={submitting}>
                {submitting ? 'Signing In...' : 'Sign In'}
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1, textTransform: 'none' }}
                onClick={() => setTab(2)}
              >
                Forgot Password?
              </Button>
            </Box>
          </TabPanel>

          {/* REGISTER + SIGN UP TAB */}
          <TabPanel value={tab} index={1}>
            {regError && <Alert severity="error" sx={{ mb: 2 }}>{regError}</Alert>}
            {regSuccess && <Alert severity="success" sx={{ mb: 2 }}>{regSuccess}</Alert>}

            <Box component="form" onSubmit={handleRegister}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                Your Details
              </Typography>
              <TextField fullWidth label="Full Name" margin="dense" required
                value={regForm.fullName} onChange={updateRegForm('fullName')} />
              <TextField fullWidth label="Email" type="email" margin="dense" required
                value={regForm.email} onChange={updateRegForm('email')}
                inputProps={{ pattern: '[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}' }}
                error={regForm.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email)}
                helperText={regForm.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email) ? 'Enter a valid email address' : ''} />
              <TextField fullWidth label="Phone Number" type="tel" margin="dense" required
                value={regForm.phone}
                onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 10); setRegForm({ ...regForm, phone: val }); }}
                inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }}
                error={regForm.phone.length > 0 && regForm.phone.length !== 10}
                helperText={regForm.phone.length > 0 && regForm.phone.length !== 10 ? 'Phone number must be 10 digits' : ''} />
              <TextField fullWidth label="Password" type="password" margin="dense" required
                value={regForm.password} onChange={updateRegForm('password')} />
              <TextField fullWidth label="Confirm Password" type="password" margin="dense" required
                value={regForm.confirmPassword} onChange={updateRegForm('confirmPassword')} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Apartment Details
              </Typography>
              <TextField fullWidth label="Apartment Name" margin="dense" required
                value={regForm.apartmentName} onChange={updateRegForm('apartmentName')}
                placeholder="e.g. Sunshine Residency" />
              <TextField fullWidth label="Address" margin="dense" required
                value={regForm.apartmentAddress} onChange={updateRegForm('apartmentAddress')} />

              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={4}>
                  <TextField fullWidth label="No. of Floors" type="number" size="small"
                    value={regForm.totalFloors} onChange={handleFloorChange('totalFloors')}
                    inputProps={{ min: 1 }}
                    helperText="Optional" />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="Flats/Floor" type="number" size="small"
                    value={regForm.flatsPerFloor} onChange={handleFloorChange('flatsPerFloor')}
                    inputProps={{ min: 1 }}
                    helperText="Optional" />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="Total Flats" type="number" size="small" required
                    value={regForm.totalFlats} onChange={updateRegForm('totalFlats')}
                    inputProps={{ min: 1 }}
                    helperText="Required" />
                </Grid>
              </Grid>

              {/* Show generated flat numbers preview */}
              {generatedFlats.length > 0 && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Flat numbers that will be available ({generatedFlats.length} flats):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {generatedFlats.map(flat => (
                      <Chip
                        key={flat}
                        label={flat}
                        size="small"
                        variant={flat === regForm.flatNumber ? 'filled' : 'outlined'}
                        color={flat === regForm.flatNumber ? 'primary' : 'default'}
                        onClick={() => setRegForm({ ...regForm, flatNumber: flat })}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <TextField fullWidth label="Your Flat Number" margin="dense" required
                value={regForm.flatNumber} onChange={updateRegForm('flatNumber')}
                placeholder={generatedFlats.length > 0 ? 'Click a flat above or type manually' : 'e.g. 101'}
                helperText={generatedFlats.length > 0 ? 'Select from above or enter manually' : ''} />

              <TextField fullWidth label="Opening Fund Balance (₹)" type="number" margin="dense"
                value={regForm.openingFundBalance} onChange={updateRegForm('openingFundBalance')}
                placeholder="e.g. 50000"
                helperText="Total fund your apartment currently has (optional, can add later)" />

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={submitting}>
                {submitting ? 'Signing Up...' : 'Sign Up'}
              </Button>
            </Box>
          </TabPanel>

          {/* FORGOT PASSWORD TAB */}
          <TabPanel value={tab} index={2}>
            {forgotError && <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert>}
            {forgotMessage && <Alert severity="success" sx={{ mb: 2 }}>{forgotMessage}</Alert>}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your registered email or phone number. We'll send a password reset link to your registered email.
            </Typography>

            <Box component="form" onSubmit={handleForgotPassword}>
              <TextField
                fullWidth label="Email or Phone Number" margin="normal" required
                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="Enter your email or 10-digit phone number"
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }}>
                Send Reset Link
              </Button>
              <Button fullWidth sx={{ mt: 1, textTransform: 'none' }} onClick={() => setTab(0)}>
                Back to Sign In
              </Button>
            </Box>
          </TabPanel>
        </Paper>
        <Typography variant="body2" align="center" sx={{ mt: 2, color: '#fff', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
          For any queries, contact us at{' '}
          <a href="mailto:info@flatexpense.com" style={{ color: '#FFD700', fontWeight: 'bold', textDecoration: 'underline' }}>info@flatexpense.com</a>
        </Typography>
      </Box>
    </Container>
    </Box>
  );
}
